import { InputCpfCnpj } from "areas/payment/inputCpfCnpj/InputCpfCnpj";
import { InputField } from "areas/payment/inputField/InputField";
import { Kiosk } from "domains/kiosk/aggregates/Kiosk";
import { ICustomer } from "modules/order/domain/models/ICustomer";
import React, {
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import {
  ICreateDefferedPromise,
  createDefferedPromise,
} from "utils/DefferedPromise";
import styles from "./CustomerIdentification.module.scss";
import { useAppInsights } from "Infrastructure/repositories/appInsights/AppInsights";
import BottomModal from "components/bottomModal/BottomModal";
import { KeyboardType } from "components/keyboard/Keyboard";
import { useUi } from "application/contexts/ui/UIContext";
import UserRegisterService from "modules/cpfRegister/service/UserRegisterService";
import { HttpRepository } from "Infrastructure/repositories/api/_HttpRepository";
import { useKiosk } from "application/contexts/kiosk/KioskContext";
import { IUserRegiterResponseDTO } from "modules/cpfRegister/interfaces/IUserRegiter";

type IInputType = "CPF" | "PHONE";

interface IIdentificationField {
  name: string;
  required?: boolean;
}

interface IIdentificationFields {
  [x: string]: IIdentificationField;
}

export interface ICustomerIdentificationHandler {
  open(
    kiosk: Kiosk,
    recharge?: "Recharge" | "Payment" | "WalletRecharge"
  ): Promise<ICustomer>;
  abort(): void;
}

interface ICustomerIdentificationProps { }

const CustomerIdentification: React.ForwardRefRenderFunction<
  ICustomerIdentificationHandler,
  ICustomerIdentificationProps
> = (_, ref) => {
  const deffered = useRef<ICreateDefferedPromise<ICustomer>>(
    createDefferedPromise()
  );

  const { addLog } = useAppInsights();
  const { hideLoading, showLoading } = useUi();
  const { kiosk } = useKiosk();

  const [visible, setVisible] = useState(false);
  const [customerFields, setCustomerFields] = useState<IIdentificationFields>(
    {}
  );
  const customer = useRef<ICustomer>({});
  const [currentStep, setCurrentStep] = useState(0);

  const totalFields = Object.keys(customerFields).length;

  const handleClose = useCallback(() => {
    setVisible(false);
    deffered.current.rejecter("Fechado pelo usuário.");
  }, []);

  const getCustomerFields = useCallback(
    (kiosk: Kiosk, recharge?: "Recharge" | "Payment" | "WalletRecharge") => {
      let fields: IIdentificationFields = {};

      // Campos identificação do cliente
      kiosk.customerField.forEach((it) => {
        const required = it.endsWith("*");
        const name = required ? it.slice(0, -1) : it;

        if (name.length) {
          fields[name] = {
            name,
            required,
          };
        }
      });

      if (kiosk.prism && !recharge) {
        fields["prism"] = {
          name: "prism",
          required: true,
        };
      }

      if (kiosk.enableCpfDiscount) {
        if (fields["documento"] === undefined) {
          fields["documento"] = {
            name: "documento",
          };
        }
      }

      if (Object.keys(fields).length === 0) {
        setVisible(false);
        deffered.current.resolver({} as ICustomer);
      }

      setCustomerFields(fields);
    },
    []
  );

  const open = useCallback(
    (kiosk: Kiosk, recharge?: "Recharge" | "Payment" | "WalletRecharge") => {
      deffered.current = createDefferedPromise<ICustomer>();
      setVisible(true);

      getCustomerFields(kiosk, recharge);
      return deffered.current.promise;
    },
    [getCustomerFields]
  );

  const abort = useCallback(() => {
    setVisible(false);
    deffered.current.rejecter(new Error("Fechado pelo usuário"));
  }, []);

  useImperativeHandle(ref, () => ({ open, abort }));

  const getKeyboardType = useCallback(
    (field: IIdentificationField): KeyboardType => {
      if (field.name.includes("email") || field.name.includes("e-mail")) {
        return "email";
      }
      if (
        field.name.includes("cpf") ||
        field.name.includes("documento") ||
        field.name.includes("telefone")
      ) {
        return "clear";
      }
      if (field.name.includes("nome")) {
        return "text";
      } else {
        return "complete";
      }
    },
    []
  );

  const getInputType = useCallback(
    (field: IIdentificationField): IInputType | undefined => {
      if (field.name.includes("telefone")) {
        return "PHONE";
      }
      if (field.name.includes("cpf") || field.name.includes("documento")) {
        return "CPF";
      }
    },
    []
  );

  const getFieldLabel = useCallback((field: IIdentificationField) => {
    if (field.name.includes("prism")) {
      return (
        <>
          Informe a <b>sua mesa</b> para receber seu pedido.
        </>
      );
    }
    if (field.name.includes("documento") || field.name.includes("cpf")) {
      return (
        <>
          Informe seu <b>CPF</b> para ter uma experiência{" "}
          <b>ainda mais incrível!</b>
        </>
      );
    }
    if (
      field.name.includes("data") ||
      field.name.includes("aniversario") ||
      field.name.includes("nascimento")
    ) {
      return (
        <>
          Informe seu <b>aniversário.</b>
        </>
      );
    }
    if (field.name.includes("nome")) {
      return (
        <>
          Qual seu <b>nome?</b>
        </>
      );
    }
    if (field.name.includes("email")) {
      return (
        <>
          Qual seu <b>email?</b>
        </>
      );
    }
    if (field.name.includes("identificador")) {
      return (
        <>
          Qual seu <b>identificador?</b>
        </>
      );
    }
    if (field.name.includes("endereco")) {
      return (
        <>
          Qual seu <b>endereço?</b>
        </>
      );
    }
    if (field.name.includes("etiqueta")) {
      return (
        <>
          Qual sua <b>etiqueta?</b>
        </>
      );
    }
    if (field.name.includes("telefone")) {
      return (
        <>
          <b>Telefone</b> para contato.
        </>
      );
    }
    return (
      <>
        Informe seu(sua) <b>{field.name}</b>
      </>
    );
  }, []);

  const handleStep = useCallback(() => {
    if (currentStep < totalFields) {
      const fieldName = Object.keys(customerFields)[currentStep];
      const field = customerFields[fieldName];

      const askInformNfce =
        field.name.includes("informCPF") ||
        field.name.includes("cpf") ||
        field.name.includes("documento");

      const onClickNext = () => {
        setCurrentStep((prev) => {
          if (totalFields - prev === 1) {
            addLog("customerFields", {
              ...Object.fromEntries(
                Object.entries(customer.current).map(([key, value]) => [
                  key,
                  !!value,
                ])
              ),
            });
            setVisible(false);
            setCustomerFields({});
            const customerFields = customer.current;
            customer.current = {};
            setCurrentStep(0);
            deffered.current.resolver(customerFields);
            return totalFields;
          } else {
            return prev + 1;
          }
        });
      };

      const handleSaveData = (value?: string) => {
        if (field.name.includes("nome")) {
          customer.current = {
            ...customer.current,
            name: value,
          };
        }
        if (field.name.includes("telefone")) {
          customer.current = {
            ...customer.current,
            phone: value,
          };
        }
        if (field.name.includes("cpf") || field.name.includes("documento")) {
          customer.current = {
            ...customer.current,
            document: value,
          };
        }
        if (field.name.includes("email")) {
          customer.current = {
            ...customer.current,
            email: value,
          };
        }
        if (field.name.includes("prism")) {
          customer.current = {
            ...customer.current,
            prism: value,
          };
        }
        if (field.name.includes("identificador")) {
          customer.current = {
            ...customer.current,
            clientIdentificator: value,
          };
        }

        onClickNext();
      };

      if (askInformNfce) {
        const saveDocument = async (document?: string) => {
          let user: IUserRegiterResponseDTO;
          try {
            const httpRepository = HttpRepository();
            const userRegisterService = UserRegisterService(httpRepository);
            showLoading();
            user = await userRegisterService.getUserData(kiosk?.localId ?? "", document ?? "");

            customer.current = {
              ...customer.current,
              documentToNf: document,
              document,
              informCPF: true,
              name: user.Nome,
              email: user.Email,
              phone: user.Telefone,
            };
          } catch (error) {
            addLog("CustomerIdentification_Error", { error });
            customer.current = {
              ...customer.current,
              documentToNf: document,
              document,
              informCPF: true,
            };
          } finally {
            hideLoading();
          }


          onClickNext();
        };
        return (
          <div className={styles.nfceContainer}>
            <InputCpfCnpj
              required={field.required}
              onPressContinuar={saveDocument}
              value={customer.current.documentToNf ?? undefined}
            />
          </div>
        );
      } else {
        return (
          <div>
            <span className={styles.inputTitle}>{getFieldLabel(field)}</span>
            <InputField
              description=""
              keyboardType={getKeyboardType(field)}
              type={getInputType(field)}
              required={field.required}
              minInputLength={
                field.name.includes("cpf") || field.name.includes("documento")
                  ? 11
                  : field.name.includes("prism")
                    ? 1
                    : 3
              }
              maxInputLength={
                field.name.includes("cpf") || field.name.includes("documento")
                  ? 11
                  : 30
              }
              onPressContinuar={handleSaveData}
              onPressCancel={handleSaveData}
              hideClear
            />
          </div>
        );
      }
    }
  }, [
    currentStep,
    totalFields,
    customerFields,
    addLog,
    getFieldLabel,
    getKeyboardType,
    getInputType,
    hideLoading,
    kiosk,
    showLoading,
  ]);

  return (
    <BottomModal showModal={visible} onClickClose={handleClose}>
      <div className={styles.inputContainer}>{handleStep()}</div>
    </BottomModal>
  );
};
export default React.forwardRef(CustomerIdentification);
